import React, { useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import ChecklistStatusDot from '../../reportCards/ChecklistStatusDot';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import { formatContextDate, formatMoney } from '../../shared/checklistUtils';
import type { ManualApprovalState } from '../../types';

import BooleanFlag from './BooleanFlag';
import type { SurchargeTableRow, SurchargesChecklistRow } from './types';

interface Props {
  flaggedRules: Array<SurchargesChecklistRow>;
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<SurchargeTableRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
}

export default function SurchargesReportCard({
  flaggedRules,
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
}: Props) {
  const [expandedRooms, setExpandedRooms] = useState<Record<string, boolean>>({});

  return (
    <ReportCard>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell>Surcharge Name</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Converted Cost</TableCell>
              <TableCell>Surcharge Price</TableCell>
              <TableCell>Accumulate</TableCell>
              <TableCell>Inside Travel Period</TableCell>
              <TableCell>Blackout Overlap</TableCell>
              <TableCell>Rooms</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={10} label="No surcharges match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = !!manualApprovals[row.id];
                const approved = row.status === 'approved' || manuallyApproved;
                const roomsExpanded = !!expandedRooms[row.id];

                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover sx={approved ? { bgcolor: 'action.hover', opacity: 0.7 } : undefined}>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {row.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatContextDate(row.fromDate)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatContextDate(row.toDate)}</TableCell>
                      <TableCell>{formatMoney(row.convertedCost, row.currencyCode)}</TableCell>
                      <TableCell>{formatMoney(row.surchargePrice, row.currencyCode)}</TableCell>
                      <TableCell>
                        <BooleanFlag falseIsGood value={row.accumulate} />
                      </TableCell>
                      <TableCell>
                        {row.insideTravelPeriod === null ? (
                          <Typography variant="body2" color="text.disabled">
                            N/A
                          </Typography>
                        ) : (
                          <BooleanFlag value={row.insideTravelPeriod} />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.blackoutOverlap === null ? (
                          <Typography variant="body2" color="text.disabled">
                            N/A
                          </Typography>
                        ) : (
                          <BooleanFlag falseIsGood value={row.blackoutOverlap} />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.dealOptionNames.length === 0 ? (
                          <Typography variant="body2" color="text.disabled">
                            N/A
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="text"
                            endIcon={roomsExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            onClick={() => setExpandedRooms((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}
                          >
                            {roomsExpanded
                              ? `Hide ${row.dealOptionNames.length} room${row.dealOptionNames.length === 1 ? '' : 's'}`
                              : `View ${row.dealOptionNames.length} room${row.dealOptionNames.length === 1 ? '' : 's'}`}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        {!approved && row.needsReview && (
                          <Button
                            size="small"
                            startIcon={<CheckIcon fontSize="small" />}
                            variant="contained"
                            onClick={() => onApprovalChange(row.id)}
                          >
                            Approve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {row.dealOptionNames.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ p: 0, borderBottom: roomsExpanded ? undefined : 'none' }}>
                          <Collapse in={roomsExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1.5 }}>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                                sx={{ letterSpacing: '0.08em', display: 'block', mb: 1 }}
                              >
                                APPLIES TO
                              </Typography>
                              <Stack direction="row" flexWrap="wrap" gap={1}>
                                {row.dealOptionNames.map((name) => (
                                  <Chip key={name} label={name} size="small" variant="outlined" />
                                ))}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <HiddenApprovedFooter count={hiddenApprovedCount} noun="Surcharge" showAutoApproved={showAutoApproved} />

      {flaggedRules.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {flaggedRules.map((rule) => {
            const ruleApproved = rule.status === 'approved' || !!manualApprovals[rule.id];
            const canApprove = !ruleApproved && rule.status === 'needsApproval';

            return (
              <Stack
                key={rule.id}
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{
                  bgcolor: 'background.default',
                  border: 1,
                  borderColor: ruleApproved ? 'divider' : 'warning.main',
                  borderRadius: 1,
                  gap: 2,
                  p: 1.5,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                  <FlagOutlinedIcon
                    fontSize="small"
                    sx={{
                      color: ruleApproved ? 'text.disabled' : 'warning.main',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {rule.name}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getRuleDescription(rule.field)}
                    </Typography>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={2} sx={{ mt: 0.75 }}>
                      {rule.details.map((detail) => (
                        <Typography key={detail.label} variant="caption" color="text.secondary">
                          <strong>{detail.label}:</strong> {detail.value}
                        </Typography>
                      ))}
                      <ChecklistStatusDot status={ruleApproved ? 'approved' : rule.status} />
                    </Stack>
                  </Box>
                </Stack>
                {canApprove && (
                  <Button
                    size="small"
                    startIcon={<CheckIcon />}
                    sx={{ flexShrink: 0 }}
                    variant="contained"
                    onClick={() => onApprovalChange(rule.id)}
                  >
                    Approve
                  </Button>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    </ReportCard>
  );
}

function getRuleDescription(field: string): string {
  switch (field) {
    case 'Overlapping Surcharge Dates':
      return 'Flag for approval if any surcharge date ranges overlap.';
    case 'Surcharge Coverage':
      return 'Cumulative surcharge dates must cover less than 75% of the travel period.';
    default:
      return '';
  }
}
